/**
 * BulkEnrolContactsDialog — GHL-style contact picker for an automation.
 *
 * Used from the automation detail page. Broker filters contacts, ticks
 * checkboxes (or "Select all matching"), and enrols the whole set.
 *
 * Design decisions:
 *   - Shows contacts by default (no need to type first) — sorted by most
 *     recently updated.
 *   - Real filters (status, owner) match the ContactsListPage filters so
 *     brokers use a familiar mental model.
 *   - Checkboxes + select-all-visible + counts. GHL parity.
 *   - Serialised enrol under the hood so we don't hammer the API; UI
 *     shows progress "Enrolled 12 of 30…".
 */
import { useEffect, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  Loader2,
  AlertCircle,
  Search,
  Zap,
  CheckCircle2,
  Filter,
  Users,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import {
  automationsApi,
  contactsApi,
  usersApi,
  ApiError,
  type ContactStatus,
} from "@/lib/api"
import { cn } from "@/lib/utils"

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  automationId: string
  automationName: string
  onDone?: (result: { enrolled: number; skipped: number; failed: number }) => void
}

const STATUS_OPTIONS: { value: ContactStatus | ""; label: string }[] = [
  { value: "", label: "All statuses" },
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "active", label: "Active" },
  { value: "client", label: "Client" },
  { value: "inactive", label: "Inactive" },
]

function useDebounced<T>(value: T, ms = 250): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return v
}

export function BulkEnrolContactsDialog({
  open,
  onOpenChange,
  automationId,
  automationName,
  onDone,
}: Props) {
  const [query, setQuery] = useState("")
  const debouncedQuery = useDebounced(query, 250)
  const [statusFilter, setStatusFilter] = useState<ContactStatus | "">("")
  const [ownerFilter, setOwnerFilter] = useState<string>("")
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [progress, setProgress] = useState<{
    done: number
    total: number
    enrolled: number
    skipped: number
    failed: number
  } | null>(null)
  const [successResult, setSuccessResult] = useState<{
    enrolled: number
    enrolledFireToday: number
    enrolledFuture: number
    earliestFuture: string | null
    latestFuture: string | null
    skipped: number
    failed: number
    skipBreakdown: {
      already_running: number
      import_reference_date_block: number
      conditions_not_met: number
      no_matching_automation: number
      trigger_params_mismatch: number
      trigger_date_in_past: number
      below_go_live_floor: number
      missing_reference_data: number
      unknown_trigger: number
    }
  } | null>(null)

  const qc = useQueryClient()

  const contacts = useQuery({
    queryKey: [
      "contacts",
      "bulk-enrol-picker",
      { q: debouncedQuery, status: statusFilter, ownerId: ownerFilter },
    ],
    queryFn: () =>
      contactsApi.list({
        q: debouncedQuery || undefined,
        status: statusFilter || undefined,
        ownerId: ownerFilter || undefined,
        pageSize: 100,
      }),
    enabled: open,
    staleTime: 15_000,
  })

  const brokers = useQuery({
    queryKey: ["users-active"],
    queryFn: () => usersApi.list(),
    enabled: open,
    staleTime: 60_000,
  })

  const items = contacts.data?.items ?? []
  const totalMatching = contacts.data?.total ?? items.length
  const showingCount = items.length

  // Reset selections when filters change (so you don't accidentally enrol
  // yesterday's selection that's now off-screen).
  useEffect(() => {
    setSelectedIds(new Set())
  }, [debouncedQuery, statusFilter, ownerFilter])

  // Reset dialog state when it opens/closes.
  useEffect(() => {
    if (!open) {
      setQuery("")
      setStatusFilter("")
      setOwnerFilter("")
      setSelectedIds(new Set())
      setErrorMsg(null)
      setProgress(null)
      setSuccessResult(null)
    }
  }, [open])

  const allVisibleSelected =
    items.length > 0 && items.every((c) => selectedIds.has(c.id))
  const someVisibleSelected = items.some((c) => selectedIds.has(c.id))

  const toggleSelectAllVisible = () => {
    setSelectedIds((cur) => {
      const next = new Set(cur)
      if (allVisibleSelected) {
        for (const c of items) next.delete(c.id)
      } else {
        for (const c of items) next.add(c.id)
      }
      return next
    })
  }

  const toggleOne = (id: string) => {
    setSelectedIds((cur) => {
      const next = new Set(cur)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const contactLabel = (c: {
    firstName?: string | null
    lastName?: string | null
    businessName?: string | null
    email?: string | null
    mobile?: string | null
  }) => {
    const name = [c.firstName, c.lastName].filter(Boolean).join(" ").trim()
    if (name) return name
    return c.businessName || c.email || c.mobile || "Unnamed contact"
  }

  const enrolAll = useMutation({
    mutationFn: async () => {
      const ids = [...selectedIds]
      let enrolled = 0
      let enrolledFireToday = 0
      let enrolledFuture = 0
      let earliestFuture: string | null = null
      let latestFuture: string | null = null
      let skipped = 0
      let failed = 0
      const skipBreakdown = {
        already_running: 0,
        import_reference_date_block: 0,
        conditions_not_met: 0,
        no_matching_automation: 0,
        trigger_params_mismatch: 0,
        trigger_date_in_past: 0,
        below_go_live_floor: 0,
        missing_reference_data: 0,
        unknown_trigger: 0,
      }
      setProgress({ done: 0, total: ids.length, enrolled: 0, skipped: 0, failed: 0 })
      for (let i = 0; i < ids.length; i++) {
        const contactId = ids[i]
        try {
          // confirmFuture: true — clicking "Enrol contacts" is an explicit
          // broker action. Under the new dispatch gate (incident 2026-08-06),
          // enrolling schedules against the contact's REAL trigger date;
          // future dates are perfectly fine and don't need a re-confirm.
          const res = await automationsApi.enroll(automationId, {
            contactId,
            confirmFuture: true,
          })
          if (res.enrolled > 0) {
            enrolled += res.enrolled
            if (res.scheduledFor && res.scheduled) {
              enrolledFuture += 1
              if (!earliestFuture || res.scheduledFor < earliestFuture) {
                earliestFuture = res.scheduledFor
              }
              if (!latestFuture || res.scheduledFor > latestFuture) {
                latestFuture = res.scheduledFor
              }
            } else {
              enrolledFireToday += 1
            }
          } else {
            skipped += 1
            const reason = res.skipReason ?? "no_matching_automation"
            if (reason in skipBreakdown) {
              skipBreakdown[reason as keyof typeof skipBreakdown] += 1
            }
          }
        } catch {
          failed += 1
        }
        setProgress({
          done: i + 1,
          total: ids.length,
          enrolled,
          skipped,
          failed,
        })
      }
      return {
        enrolled,
        enrolledFireToday,
        enrolledFuture,
        earliestFuture,
        latestFuture,
        skipped,
        failed,
        skipBreakdown,
      }
    },
    onSuccess: (result) => {
      setSuccessResult(result)
      onDone?.(result)
      qc.invalidateQueries({ queryKey: ["automations"] })
      qc.invalidateQueries({ queryKey: ["automation", automationId] })
    },
    onError: (err) => {
      setErrorMsg(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Enrolment failed. Try again."
      )
      setProgress(null)
    },
  })

  const clearFilters = () => {
    setQuery("")
    setStatusFilter("")
    setOwnerFilter("")
  }

  const anyFilterActive = query || statusFilter || ownerFilter

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-blue-600" />
            Enrol contacts into automation
          </DialogTitle>
        </DialogHeader>

        {successResult ? (
          <div className="pt-2 space-y-3">
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                <div className="text-sm">
                  <div className="font-medium text-emerald-900">
                    Enrolled {successResult.enrolled}{" "}
                    {successResult.enrolled === 1 ? "contact" : "contacts"} into{" "}
                    <span className="font-semibold">{automationName}</span>
                  </div>
                  {successResult.enrolledFireToday > 0 && (
                    <div className="text-emerald-800/80 text-xs mt-1">
                      {successResult.enrolledFireToday} will send today (in the next scanner tick).
                    </div>
                  )}
                  {successResult.enrolledFuture > 0 && successResult.earliestFuture && (
                    <div className="text-emerald-800/80 text-xs mt-1">
                      {successResult.enrolledFuture} scheduled for a future date —
                      earliest {successResult.earliestFuture}
                      {successResult.latestFuture && successResult.latestFuture !== successResult.earliestFuture
                        ? `, latest ${successResult.latestFuture}`
                        : ""}
                      . Nothing sends before the exact trigger date.
                    </div>
                  )}
                  {(successResult.skipped > 0 || successResult.failed > 0) && (
                    <div className="text-emerald-800/80 text-xs mt-2 space-y-0.5 border-t border-emerald-200 pt-2">
                      {successResult.skipBreakdown.already_running > 0 && (
                        <div>
                          {successResult.skipBreakdown.already_running} skipped — already running in this automation
                        </div>
                      )}
                      {successResult.skipBreakdown.trigger_date_in_past > 0 && (
                        <div>
                          {successResult.skipBreakdown.trigger_date_in_past} skipped — trigger date already passed (nothing to send)
                        </div>
                      )}
                      {successResult.skipBreakdown.missing_reference_data > 0 && (
                        <div>
                          {successResult.skipBreakdown.missing_reference_data} skipped — missing data (e.g. no term_end_date or date_of_birth on record)
                        </div>
                      )}
                      {successResult.skipBreakdown.below_go_live_floor > 0 && (
                        <div>
                          {successResult.skipBreakdown.below_go_live_floor} skipped — trigger date below automation go-live floor
                        </div>
                      )}
                      {successResult.skipBreakdown.import_reference_date_block > 0 && (
                        <div>
                          {successResult.skipBreakdown.import_reference_date_block} skipped — no matching future date (imported historical data)
                        </div>
                      )}
                      {successResult.skipBreakdown.conditions_not_met > 0 && (
                        <div>
                          {successResult.skipBreakdown.conditions_not_met} skipped — entry conditions not met
                        </div>
                      )}
                      {successResult.skipBreakdown.trigger_params_mismatch > 0 && (
                        <div>
                          {successResult.skipBreakdown.trigger_params_mismatch} skipped — trigger parameters didn't match
                        </div>
                      )}
                      {successResult.skipBreakdown.no_matching_automation > 0 && (
                        <div>
                          {successResult.skipBreakdown.no_matching_automation} skipped — no matching automation
                        </div>
                      )}
                      {successResult.skipBreakdown.unknown_trigger > 0 && (
                        <div>
                          {successResult.skipBreakdown.unknown_trigger} skipped — unknown trigger type
                        </div>
                      )}
                      {successResult.failed > 0 && (
                        <div className="text-red-800">{successResult.failed} failed</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex justify-end">
              <Button onClick={() => onOpenChange(false)}>Done</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3 pt-2">
            <p className="text-sm text-muted-foreground">
              Pick contacts to enrol into{" "}
              <span className="font-medium text-foreground">{automationName}</span>. Contacts
              already running in this automation are skipped automatically.
            </p>

            {/* Filters */}
            <div className="flex flex-col gap-2 md:flex-row md:items-center">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search name, business, email, mobile…"
                  className="pl-8"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  disabled={enrolAll.isPending}
                />
              </div>
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <select
                  className="border-input bg-background h-9 rounded-md border px-2 text-sm"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as ContactStatus | "")}
                  disabled={enrolAll.isPending}
                >
                  {STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <select
                  className="border-input bg-background h-9 rounded-md border px-2 text-sm"
                  value={ownerFilter}
                  onChange={(e) => setOwnerFilter(e.target.value)}
                  disabled={enrolAll.isPending}
                >
                  <option value="">All brokers</option>
                  {brokers.data?.users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.fullName}
                    </option>
                  ))}
                </select>
                {anyFilterActive && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="text-xs text-blue-600 hover:text-blue-700 underline underline-offset-2"
                    disabled={enrolAll.isPending}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            {/* Select-all row + counts */}
            <div className="flex items-center justify-between px-1">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-input"
                  checked={allVisibleSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someVisibleSelected && !allVisibleSelected
                  }}
                  onChange={toggleSelectAllVisible}
                  disabled={enrolAll.isPending || items.length === 0}
                />
                <span className="text-muted-foreground">
                  {allVisibleSelected ? "Deselect visible" : "Select all visible"}
                </span>
              </label>
              <div className="text-xs text-muted-foreground">
                {contacts.isLoading ? (
                  "Loading…"
                ) : (
                  <>
                    <span className="font-medium text-foreground">{selectedIds.size}</span>{" "}
                    selected · showing {showingCount} of {totalMatching}
                    {totalMatching > showingCount && " (refine filters to see more)"}
                  </>
                )}
              </div>
            </div>

            {/* Contact list */}
            <div className="max-h-80 overflow-y-auto rounded-md border border-border">
              {contacts.isLoading ? (
                <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading contacts…
                </div>
              ) : items.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground">
                  <Users className="mx-auto h-8 w-8 mb-2 opacity-40" />
                  {anyFilterActive
                    ? "No contacts match those filters."
                    : "No contacts yet."}
                </div>
              ) : (
                <ul className="divide-y divide-border">
                  {items.map((c) => {
                    const isSelected = selectedIds.has(c.id)
                    return (
                      <li key={c.id}>
                        <label
                          className={cn(
                            "flex items-center gap-3 px-3 py-2 text-sm cursor-pointer transition-colors",
                            isSelected ? "bg-blue-50/60" : "hover:bg-accent"
                          )}
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-input"
                            checked={isSelected}
                            onChange={() => toggleOne(c.id)}
                            disabled={enrolAll.isPending}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-foreground truncate">
                              {contactLabel(c)}
                            </div>
                            <div className="text-xs text-muted-foreground truncate">
                              {[c.businessName, c.email, c.mobile]
                                .filter(Boolean)
                                .join(" · ") || "—"}
                            </div>
                          </div>
                          <div className="text-[10px] uppercase tracking-wide text-muted-foreground shrink-0">
                            {c.status}
                          </div>
                        </label>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>

            {progress && enrolAll.isPending && (
              <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Enrolling {progress.done} of {progress.total}… ({progress.enrolled}{" "}
                  enrolled, {progress.skipped} skipped)
                </div>
              </div>
            )}

            {errorMsg && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
                <div className="flex items-start gap-2">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
                  <div>{errorMsg}</div>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={enrolAll.isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={() => enrolAll.mutate()}
                disabled={selectedIds.size === 0 || enrolAll.isPending}
              >
                {enrolAll.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Enrolling…
                  </>
                ) : (
                  `Enrol ${selectedIds.size || ""}`.trim()
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
